/**
 * Cloudflare Turnstile token provider for the public HubSpot proxy.
 *
 * security/0.2c (Option 4c). /api/hubspot-request is reachable anonymously so
 * the WordPress marketing iframe can record resource views. Turnstile raises the
 * cost of driving that endpoint from a script without putting a login in front
 * of it.
 *
 * Design notes:
 *  - Completely inert unless VUE_APP_TURNSTILE_SITEKEY is set at build time.
 *    getTurnstileToken() then resolves to null and the axios interceptor sends
 *    the request unchanged, so a build without the key behaves exactly as before.
 *  - Never throws and never hangs: every path is wrapped, and waiting for a
 *    token is bounded by READY_TIMEOUT_MS. Tracking is best-effort; a failure
 *    here must not break the page.
 *  - The Cloudflare script is injected on first use rather than from index.html,
 *    so pages that never call the proxy don't pay for it.
 *  - Tokens are single-use at Cloudflare. The API caches a successful verdict
 *    for ~4 minutes, which is how the 3-4 sequential calls of one resource view
 *    share a single token. We refresh at 3 minutes to stay inside both that
 *    cache and Cloudflare's own ~5 minute token lifetime.
 */

const SITEKEY = process.env.VUE_APP_TURNSTILE_SITEKEY;
const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TOKEN_MAX_AGE_MS = 3 * 60 * 1000;
const READY_TIMEOUT_MS = 8000;

let scriptPromise = null;
let widgetId = null;
let containerEl = null;
let currentToken = null;
let tokenIssuedAt = 0;
let inFlight = null;

export function turnstileEnabled() {
  return Boolean(SITEKEY);
}

function loadScript() {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const el = document.createElement("script");
    el.src = SCRIPT_URL;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error("Turnstile script failed to load"));
    document.head.appendChild(el);
  });

  return scriptPromise;
}

function ensureContainer() {
  if (containerEl && document.body.contains(containerEl)) return containerEl;

  containerEl = document.createElement("div");
  containerEl.id = "cf-turnstile-container";
  // Kept out of layout; the widget is configured as invisible /
  // interaction-only in the Cloudflare dashboard.
  containerEl.style.position = "absolute";
  containerEl.style.width = "0";
  containerEl.style.height = "0";
  containerEl.style.overflow = "hidden";
  containerEl.setAttribute("aria-hidden", "true");
  document.body.appendChild(containerEl);

  return containerEl;
}

/** Resolve once the widget hands us a token, or null on timeout/error. */
function awaitToken(trigger) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const timer = setTimeout(() => finish(null), READY_TIMEOUT_MS);

    const onToken = (token) => {
      clearTimeout(timer);
      currentToken = token || null;
      tokenIssuedAt = currentToken ? Date.now() : 0;
      finish(currentToken);
    };

    const onFailure = () => {
      clearTimeout(timer);
      currentToken = null;
      tokenIssuedAt = 0;
      finish(null);
    };

    try {
      trigger(onToken, onFailure);
    } catch (e) {
      onFailure();
    }
  });
}

export async function getTurnstileToken() {
  if (!turnstileEnabled()) return null;

  const fresh =
    currentToken && Date.now() - tokenIssuedAt < TOKEN_MAX_AGE_MS;
  if (fresh) return currentToken;

  // Collapse concurrent callers onto one challenge.
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      await loadScript();
      if (!window.turnstile) return null;

      if (widgetId === null) {
        return await awaitToken((onToken, onFailure) => {
          widgetId = window.turnstile.render(ensureContainer(), {
            sitekey: SITEKEY,
            callback: onToken,
            "error-callback": onFailure,
            "timeout-callback": onFailure,
            "expired-callback": () => {
              currentToken = null;
              tokenIssuedAt = 0;
            },
            appearance: "interaction-only",
          });
          if (widgetId === undefined || widgetId === null) onFailure();
        });
      }

      // Existing widget: ask Cloudflare for a new single-use token.
      return await awaitToken((onToken, onFailure) => {
        try {
          window.turnstile.reset(widgetId);
        } catch (e) {
          onFailure();
          return;
        }
        const poll = setInterval(() => {
          let value;
          try {
            value = window.turnstile.getResponse(widgetId);
          } catch (e) {
            value = null;
          }
          if (value) {
            clearInterval(poll);
            onToken(value);
          }
        }, 150);
        setTimeout(() => clearInterval(poll), READY_TIMEOUT_MS);
      });
    } catch (e) {
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}
